import { Outlet, NavLink, useNavigate, Navigate, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import apiClient from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { LayoutDashboard, FileText, Calendar, MessageSquare, MessagesSquare, Newspaper, LogOut, Home as HomeIcon, Sparkles, ScrollText, Inbox } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) return;
    let mounted = true;
    const fetchUnread = () => {
      apiClient.get("/admin/inbox/counts")
        .then((r) => { if (mounted) setUnread(r.data.unread || 0); })
        .catch(() => {});
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    return () => { mounted = false; clearInterval(t); };
  }, [user]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading...</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-5 py-3 text-sm transition-colors ${
      isActive
        ? "bg-[#29324A] text-white"
        : "text-slate-300 hover:bg-white/5 hover:text-white"
    }`;

  return (
    <div className="min-h-screen flex bg-[#F8F9FA]">
      <aside className="w-64 bg-[#29324A] text-white flex flex-col shrink-0">
        <div className="p-5 border-b border-white/10">
          <Link to="/admin" className="flex items-center gap-3">
            <div className="bg-white p-1.5">
              <img src="/assets/beyond-borders-logo.png" alt="Shamima Yasmin Anwar" className="h-7 w-auto" />
            </div>
            <div>
              <div className="text-[10px] font-bold tracking-widest text-[#C43A89] uppercase">Admin</div>
              <div className="text-xs text-slate-300">Console</div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 py-4">
          <NavLink to="/admin" end data-testid="admin-nav-dashboard" className={linkClass}>
            <LayoutDashboard className="h-4 w-4" /> Dashboard
          </NavLink>
          <NavLink to="/admin/inbox" data-testid="admin-nav-inbox" className={linkClass}>
            <Inbox className="h-4 w-4" /> All Leads &amp; Inbox
            {unread > 0 && (
              <span data-testid="inbox-unread-badge" className="ml-auto bg-[#C43A89] text-white text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full">
                {unread}
              </span>
            )}
          </NavLink>
          <NavLink to="/admin/pr-assessments" data-testid="admin-nav-pr" className={linkClass}>
            <FileText className="h-4 w-4" /> PR Assessments
          </NavLink>
          <NavLink to="/admin/student-pr-assessments" data-testid="admin-nav-student-pr" className={linkClass}>
            <FileText className="h-4 w-4" /> Student PR Assessments
          </NavLink>
          <NavLink to="/admin/skilled-migration" data-testid="admin-nav-skilled" className={linkClass}>
            <Sparkles className="h-4 w-4" /> Skilled Migration Assessment
          </NavLink>
          <NavLink to="/admin/gs-submissions" data-testid="admin-nav-gs" className={linkClass}>
            <ScrollText className="h-4 w-4" /> GS / SOP Builder
          </NavLink>
          <NavLink to="/admin/consultations" data-testid="admin-nav-consults" className={linkClass}>
            <Calendar className="h-4 w-4" /> Consultations
          </NavLink>
          <NavLink to="/admin/messages" data-testid="admin-nav-msgs" className={linkClass}>
            <MessageSquare className="h-4 w-4" /> Messages
          </NavLink>
          <NavLink to="/admin/chat-enquiries" data-testid="admin-nav-chat" className={linkClass}>
            <MessagesSquare className="h-4 w-4" /> Chat Enquiries
          </NavLink>
          <NavLink to="/admin/blog" data-testid="admin-nav-blog" className={linkClass}>
            <Newspaper className="h-4 w-4" /> Blog Manager
          </NavLink>
          <NavLink to="/admin/assessment-rules" data-testid="admin-nav-rules" className={linkClass}>
            <Newspaper className="h-4 w-4" /> 485 Assessment Rules
          </NavLink>
        </nav>

        <div className="p-4 border-t border-white/10 space-y-2">
          <Link to="/" className="flex items-center gap-3 px-3 py-2 text-xs text-slate-400 hover:text-white">
            <HomeIcon className="h-3.5 w-3.5" /> View site
          </Link>
          <button data-testid="admin-logout-btn" onClick={handleLogout} className="flex items-center gap-3 px-3 py-2 text-xs text-slate-400 hover:text-[#C43A89] w-full">
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
          <div className="px-3 text-[10px] text-slate-500 pt-2 border-t border-white/10 break-all">{user.email}</div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-x-auto">
        <Outlet />
      </main>
      <Toaster position="top-right" richColors />
    </div>
  );
}
